import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  TextInput,
  Textarea,
  Select,
  Button,
  Alert,
  Stepper,
  Divider
} from '@mantine/core';
import {
  IconBuildingStore,
  IconUser,
  IconMapPin,
  IconFileText,
  IconCheck,
  IconExclamationCircle,
  IconId
} from '@tabler/icons-react';
import { api } from '../../services/api';
import IDVerification from '../verification/IDVerification';

const SellerRegistrationForm = ({ onSuccess, onCancel }) => {
  const { user } = useSelector(state => state.auth);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    business_name: '',
    business_type: 'individual',
    business_description: '',
    business_phone: '',
    business_email: user?.email || '',
    business_address_line1: '',
    business_address_line2: '',
    business_city: '',
    business_state: '',
    business_postal_code: '',
    business_country: 'United States',
    tax_id: '',
    business_license: ''
  });

  const [verificationData, setVerificationData] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Information
        return formData.business_name.trim().length >= 2 &&
               formData.business_phone.trim().length >= 10 &&
               formData.business_email.trim().length > 0;
      case 1: // ID Verification
        return verificationData !== null;
      default:
        return true;
    }
  };

  const nextStep = async () => {
    if (validateStep(activeStep)) {
      // If moving from Princess Info (step 0) to ID Verification (step 1), 
      // create the seller profile first
      if (activeStep === 0) {
        try {
          setLoading(true);
          setError(null);
          
          // Ensure we have a CSRF token
          try {
            await api.fetchCSRFToken();
            console.log('CSRF token fetched successfully');
          } catch (csrfError) {
            console.warn('Could not fetch CSRF token, proceeding anyway:', csrfError);
          }

          // Create seller profile with basic info only (no address fields)
          const basicSellerData = {
            business_name: formData.business_name,
            business_type: formData.business_type,
            business_description: formData.business_description,
            business_phone: formData.business_phone,
            business_email: formData.business_email,
            business_country: formData.business_country || 'United States'
          };
          
          const response = await api.registerSeller(basicSellerData);
          console.log('Seller profile created successfully for ID verification');
          
          // Now move to ID verification step
          setActiveStep(prev => Math.min(prev + 1, 3));
        } catch (err) {
          console.error('Error creating seller profile:', err);
          setError(err.response?.data?.error || 'Failed to create seller profile. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        // For other steps, just move forward normally
        setActiveStep(prev => Math.min(prev + 1, 3));
      }
    }
  };

  const prevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    // At this point, seller profile is already created and ID verification is complete
    // Just show success and complete the registration flow
    setSuccess(true);
    setActiveStep(3); // Success step
    
    setTimeout(() => {
      if (onSuccess) {
        // Pass some basic info since the actual registration happened earlier
        onSuccess({ message: 'Registration completed successfully' });
      }
    }, 2000);
  };

  const renderPersonalInformation = () => (
    <Stack gap="md">
      <Title order={3}>Princess Information</Title>
      
      <TextInput
        label="Display Name"
        placeholder="How you want to be known as a princess"
        value={formData.business_name}
        onChange={(e) => handleInputChange('business_name', e.currentTarget.value)}
        required
        leftSection={<IconUser size="1rem" />}
        description="This is how buyers will see you on the marketplace"
      />

      <Textarea
        label="About You"
        placeholder="Tell buyers about yourself and what you sell"
        value={formData.business_description}
        onChange={(e) => handleInputChange('business_description', e.currentTarget.value)}
        rows={3}
        description="Optional: Describe what kinds of items you sell"
      />

      <Group grow>
        <TextInput
          label="Phone Number"
          placeholder="+1234567890"
          value={formData.business_phone}
          onChange={(e) => handleInputChange('business_phone', e.currentTarget.value)}
          required
          description="For verification purposes only"
        />

        <TextInput
          label="Contact Email"
          placeholder="your@email.com"
          value={formData.business_email}
          onChange={(e) => handleInputChange('business_email', e.currentTarget.value)}
          required
          leftSection={<IconUser size="1rem" />}
          description="How buyers can contact you"
        />
      </Group>
    </Stack>
  );


  const renderIDVerification = () => (
    <IDVerification
      onComplete={(data) => {
        setVerificationData(data);
        setActiveStep(2); // Move to review step
      }}
      onCancel={() => setActiveStep(0)} // Go back to princess info step
      loading={loading}
    />
  );

  const renderSuccess = () => (
    <Stack gap="lg" align="center">
      <IconCheck size="4rem" color="green" />
      <Title order={2} ta="center">Registration Successful!</Title>
      <Text ta="center" size="lg">
        Your princess application has been submitted for review. You'll receive an email notification 
        once your account has been verified by our team.
      </Text>
      <Text ta="center" c="dimmed">
        This usually takes 1-2 business days. Once approved, you can start listing your items!
      </Text>
    </Stack>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderPersonalInformation();
      case 1:
        return renderIDVerification();
      case 2:
        return (
          <Stack gap="md">
            <Title order={3}>Review Your Information</Title>
            <Text c="dimmed">
              Please review your information before submitting your princess application.
            </Text>
            <Alert color="blue">
              ID verification completed successfully! Your documents will be securely processed for verification.
            </Alert>
          </Stack>
        );
      case 3:
        return renderSuccess();
      default:
        return null;
    }
  };

  return (
    <Container size="md" py="xl">
      <Paper shadow="sm" radius="md" p="xl">
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Title order={1} ta="center" mb="sm">
              Become a Princess
            </Title>
            <Text ta="center" c="dimmed">
              Join PediPiggy as a verified princess and start listing your items
            </Text>
          </div>

          {/* Stepper */}
          <Stepper active={activeStep} size="sm" radius="md">
            <Stepper.Step label="Princess Info" description="About you" />
            <Stepper.Step label="ID Verification" description="Verify identity" icon={<IconId size="1rem" />} />
            <Stepper.Step label="Review" description="Final check" />
            <Stepper.Step label="Complete" description="Registration done" />
          </Stepper>

          <Divider />

          {/* Error Alert */}
          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {getStepContent()}

          {/* Navigation Buttons */}
          {activeStep < 3 && activeStep !== 1 && (
            <Group justify="space-between">
              <Group gap="sm">
                {onCancel && activeStep === 0 && (
                  <Button variant="subtle" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                {activeStep > 0 && activeStep !== 1 && (
                  <Button variant="outline" onClick={prevStep}>
                    Previous
                  </Button>
                )}
              </Group>

              <Group gap="sm">
                {activeStep < 1 ? (
                  <Button 
                    onClick={nextStep}
                    disabled={!validateStep(activeStep)}
                    loading={loading}
                  >
                    {activeStep === 0 ? 'Create Profile & Continue' : 'Next'}
                  </Button>
                ) : activeStep === 2 ? (
                  <Button 
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={!validateStep(activeStep)}
                    leftSection={<IconUser size="1rem" />}
                  >
                    Submit Registration
                  </Button>
                ) : null}
              </Group>
            </Group>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default SellerRegistrationForm;